import { withAnalytics } from 'contexts/Analytics/Analytics';
import { withApi } from 'contexts/Api/Api';
import { withPopups } from 'contexts/Popups/Popups';
import { withSettings } from 'contexts/Settings/Settings';
import { withSounds } from 'contexts/Sounds/Sounds';
import {
    AntExplorer,
    AntQueen,
    AntWarrior,
    AntWorker,
    DangerAnteater,
    DangerAntWarrior,
    DangerAntWorker,
    DangerLawnmower,
    DangerMagnifier,
    DangerSpider,
    Entity,
    Food,
    Map,
    Nature,
} from 'game';
import gsap from 'gsap';
import {
    clamp,
    compose,
    createGrid,
    getAntUnlock,
    getEntityCategoryGroup,
    getEntityTypeGroup,
    hypotenuse,
    log,
    merge,
    round,
    traverseFog,
} from 'helpers';
import * as PIXI from 'pixi.js';
import React, { createContext, useContext } from 'react';
import Confetti from 'react-confetti';
import * as setup from 'setup';

export const GameContext = createContext<GameState>({} as GameState);

export const useGame = () => useContext(GameContext);

// Exported for unit-testing the pure portfolio-allocation transition logic
// (e.g. getAntAllocationDiffs). The mounted game is used via GameProvider below.
export class Game extends React.Component<GameProviderProps, GameState> {
    app: PIXI.Application;
    frameRequest: number | null;
    completionProgressSynced = false;

    /** Whether the async pixi v8 Application.init() has completed */
    initialised = false;
    /** Whether the component unmounted before init completed */
    unmounted = false;

    previousTime = 0;
    tickTime = 0;

    constructor(props: GameProviderProps) {
        super(props);

        const { settings, sounds } = props;

        // Get fog
        const fog: MapFog = {};
        traverseFog((x, y) => (fog[`${x},${y}`] = true));

        // Set initial state
        this.state = {
            calories: setup.caloriesStart,
            caloriesAdding: 0,
            caloriesCost: 0,
            caloriesIncome: 0,
            caloriesReserve: setup.caloriesReserveStart,
            entities: {
                ants: {
                    active: [],
                    allocation: 0,
                    buffs: {
                        attack: 0,
                        carry: 0,
                        defense: 0,
                        speed: 0,
                    },
                    created: 0,
                    removed: 0,
                    explorers: {
                        active: [],
                        allocation: 0,
                        created: 0,
                        mutations: 0,
                        removed: 0,
                        share: 0,
                    },
                    queens: {
                        active: [],
                        created: 0,
                        removed: 0,
                    },
                    warriors: {
                        active: [],
                        allocation: 0,
                        created: 0,
                        removed: 0,
                        share: 0,
                    },
                    workers: {
                        active: [],
                        allocation: 0,
                        created: 0,
                        removed: 0,
                        share: 0,
                    },
                },
                dangers: {
                    active: [],
                    created: 0,
                    removed: 0,
                    anteaters: {
                        active: [],
                        created: 0,
                        removed: 0,
                    },
                    antWarriors: {
                        active: [],
                        created: 0,
                        removed: 0,
                    },
                    antWorkers: {
                        active: [],
                        created: 0,
                        removed: 0,
                    },
                    lawnmowers: {
                        active: [],
                        created: 0,
                        removed: 0,
                    },
                    magnifiers: {
                        active: [],
                        created: 0,
                        removed: 0,
                    },
                    spiders: {
                        active: [],
                        created: 0,
                        removed: 0,
                    },
                },
                foods: {
                    active: [],
                    created: 0,
                    removed: 0,
                },
                natures: {
                    active: [],
                    created: 0,
                    removed: 0,
                },
            },
            events: {},
            finished: false,
            fog,
            gridMatrix: createGrid(setup.map.cells.wide, setup.map.cells.wide, 0),
            map: null,
            paused: true,
            retries: 0,
            settings,
            snapshot: {
                index: 0,
            } as GameSnapshot,
            sounds,
            speed: 1,
            turns: 0,
            unlocks: {
                antExplorer: false,
                antExplorerMutation: false,
                antWarrior: false,
                antWorker: false,
                colonyHealth: false,
                currentRate: false,
                paradise: false,
                portfolioTool: false,
                reserveLimit: false,
                river: false,
                starvation: false,
            },
            addCalories: this.addCalories,
            changeAntAllocation: this.changeAntAllocation,
            changeCaloriesReserve: this.changeCaloriesReserve,
            createEntity: this.createEntity,
            finish: this.finish,
            focusEntity: this.focusEntity,
            getEntity: this.getEntity,
            removeEntity: this.removeEntity,
            restoreSnapshot: this.restoreSnapshot,
            retry: this.retry,
            revealFog: this.revealFog,
            setPaused: this.setPaused,
            setSpeed: this.setSpeed,
            setUnlock: this.setUnlock,
            showPopup: this.showPopup,
            takeSnapshot: this.takeSnapshot,
        };
    }

    componentDidMount() {
        void this.setupApp();
    }

    /** Creates and asynchronously initialises the pixi v8 Application */
    setupApp = async () => {
        const { settings } = this.props;

        // Create app (pixi v8 initialises asynchronously to support WebGPU)
        this.app = new PIXI.Application();
        await this.app.init({
            antialias: true,
            backgroundColor: 0x111111,
        });

        // Bail if the component unmounted while init was in flight
        if (this.unmounted) {
            this.app.destroy(true, { children: true });
            return;
        }
        this.initialised = true;
        this.app.canvas.className = 'game';

        // Add Map
        const map = new Map(this.app, this.state);
        this.app.stage.addChild(map);

        // Add natures
        const natures = setup.natures.map((natureSetup, index) => {
            const { type, ...props } = natureSetup;
            const nature = new Nature(this.state, type, props, {
                index: index + 1,
            });
            map.entities.addChild(nature);
            return nature;
        });

        // Get unlocks
        const unlocks = this.state.unlocks;
        if (settings.debugUnlocks) {
            Object.keys(unlocks)
                .filter(unlock => unlock !== 'paradise')
                .forEach(unlock => {
                    unlocks[unlock as GameUnlock] = true;
                });
        }

        // Update state and show intro
        this.setState(
            prevState => ({
                entities: merge(prevState.entities, {
                    natures: {
                        active: natures,
                        created: natures.length,
                    },
                }),
                events: this.parseEvents(),
                map,
                unlocks,
            }),
            this.init
        );
    }

    componentDidUpdate(prevProps: GameProviderProps, prevState: GameState) {
        const { entities, map, paused, finished, settings, turns } = this.state;

        // Check paused
        if (paused) {
            return;
        }

        // Update events
        this.checkEvents(prevState);

        // Update turn states
        if (turns !== prevState.turns) {
            this.checkAntAllocation();
            this.updateCalories();
            this.updateGridMatrix();
            this.checkTestTurn();
        }

        // Update settings
        if (this.props.settings !== prevProps.settings) {
            this.setState(prevState => ({
                ...prevState,
                settings: {
                    ...this.props.settings,
                },
            }));
        }

        // Update entity states
        if (
            entities !== prevState.entities ||
            finished !== prevState.finished ||
            settings !== prevState.settings ||
            turns !== prevState.turns
        ) {
            entities.ants.active.forEach(ant => ant.updateGameState(this.state));
            entities.dangers.active.forEach(danger => danger.updateGameState(this.state));
            entities.foods.active.forEach(food => food.updateGameState(this.state));
            entities.natures.active.forEach(nature => nature.updateGameState(this.state));
            map.updateGameState(this.state);
        }
    }

    componentWillUnmount() {
        // Clear request
        if (this.frameRequest) {
            window.cancelAnimationFrame(this.frameRequest);
        }

        // Remove app
        this.unmounted = true;
        if (this.app && this.initialised) {
            this.app.canvas.parentElement?.removeChild(this.app.canvas);
            this.app.destroy(true, {
                children: true,
            });
        }
    }

    init = async () => {
        // Add view
        const container = document.getElementById('game-container');
        if (container) {
            container.appendChild(this.app.canvas);
            this.app.resizeTo = container;
            this.state.map.resize();
        }

        // Update state
        await this.checkTest();
        this.checkEvents(this.state);
        this.updateGridMatrix();
        this.state.map.updateGameState(this.state);

        // Run
        this.setPaused(false);
    };

    finish = () => {
        if (this.state.finished) {
            return;
        }

        this.setPaused(true);
        this.setState({
            finished: true,
        });
        void this.syncCompletionProgress();
    };

    syncCompletionProgress = async () => {
        const { api } = this.props;

        if (this.completionProgressSynced || !api.authenticate || !api.authenticated) {
            return;
        }

        this.completionProgressSynced = true;
        await api.upsertGameProgress('portfolio-manager', {
            completed: true,
            highest_checkpoint_id: 'game_completed',
            highest_checkpoint_rank: 100,
            last_checkpoint_id: 'game_completed',
            last_checkpoint_rank: 100,
        });
    };

    parseEvents = () => {
        const { settings } = this.props;

        const entries = Object.entries(setup.events).map(([name, event]) => {
            // Remove checks if test
            if (name === settings.test) {
                delete event.after;
                delete event.condition;
                delete event.delay;
            }

            // Create event
            const e: GameEvent = {
                after: [],
                condition: () => true,
                delay: 0,
                doubleCheck: false,
                interval: 0,
                name,
                preserve: false,
                removes: [],
                repeat: false,
                onTrigger: () => null,
                ...event,
            };

            // Check afters
            e.after.forEach(after => {
                if (!setup.events[after]) {
                    console.warn(`Event '${name}' has undefined after '${after}'`);
                }
            });

            // Check removes
            e.removes.forEach(remove => {
                if (!setup.events[remove]) {
                    console.warn(`Event '${name}' removes undefined '${remove}'`);
                }
            });

            // Check popups
            const showPopups = e.onTrigger.toString().matchAll(/showPopup\('(.*?)'\)/g);
            Array.from(showPopups).forEach(match => {
                const popupName = match[1];
                if (!this.props.popups.get(popupName)) {
                    console.warn(`Event '${name}' shows undefined popup '${popupName}'`);
                }
            });

            return [name, e];
        });

        return Object.fromEntries(entries);
    };

    checkEvents = (prevState: GameState) => {
        const { events, turns } = this.state;
        const eventsList = Object.values(events);
        let updatedEvents = false;

        // Get active events
        const activeEvents = eventsList.filter(
            event =>
                event.triggerTurn !== turns &&
                !event.after.some(afterName => events[afterName]) &&
                (!event.interval || (turns !== prevState.turns && !(turns % event.interval)))
        );

        // Filter conditions met
        const readyEvents = activeEvents.filter(event => {
            if (event.ready || event.condition(this.state, prevState, event.afterState || prevState)) {
                if (!event.ready) {
                    // Update event
                    event.ready = true;
                    event.readyTurn = turns;
                    updatedEvents = true;
                }
                return true;
            }
            return false;
        });

        // Filter existing, delays and doublechecks
        const triggerEvents = readyEvents.filter(
            event =>
                events[event.name] &&
                turns >= (event.readyTurn || 0) + event.delay &&
                (!event.doubleCheck || event.condition(this.state, prevState, event.afterState || prevState))
        );

        // Process trigger events
        triggerEvents.forEach(event => {
            // Debug log trigger
            if (this.props.settings.debugEvents) {
                log(turns, 'Event', `Triggered '${event.name}'`);
            }

            // Trigger event
            event.onTrigger(this.state, prevState, event.afterState || prevState);
            event.triggerTurn = turns;

            // Track event
            void this.props.analytics.trackEvent('game_event', {
                event_name: event.name,
                turns,
            });
            void this.props.analytics.trackActionEvent('action_tool_step_completed', {
                resource_id: event.name,
                resource_type: 'step',
                source: 'game_event',
            });
            if (/(complete|end|finish)$/i.test(event.name)) {
                void this.props.analytics.trackActionEvent('action_tool_completed', {
                    resource_id: event.name,
                    resource_type: 'tool',
                    source: 'game_event',
                });
            }

            // Remove name from afters
            eventsList
                .filter(afterEvent => afterEvent.after.includes(event.name))
                .forEach(afterEvent => (afterEvent.afterState = this.state));

            // Process removals
            event.removes.forEach(removeName => {
                delete events[removeName];
            });

            // Repeat or remove
            if (event.repeat) {
                delete event.afterState;
                delete event.ready;
                delete event.readyTurn;
            } else {
                delete events[event.name];
            }

            updatedEvents = true;
        });

        // Update state
        if (updatedEvents) {
            this.setState({
                events: {
                    ...events,
                },
            });
        }
    };

    checkTest = async () => {
        return new Promise<void>(async resolve => {
            const { settings } = this.props;

            // Check test
            const test = (settings.test && setup.tests[settings.test]) || null;
            if (test) {
                // Get state snapshot
                const snapshot = (await this.takeSnapshot(true)) as GameSnapshot;

                // Check test snapshot
                if (test.snapshot) {
                    // Set properties
                    const { entities: snapshotEntities, ...snapshotState } = test.snapshot;
                    snapshot.calories = snapshotState.calories || snapshot.calories;
                    snapshot.caloriesReserve = snapshotState.caloriesReserve || snapshot.caloriesReserve;
                    snapshot.turns = snapshotState.turns || snapshot.turns;

                    // Check entities
                    if (test.snapshot.entities && Object.keys(test.snapshot.entities).length) {
                        snapshot.entities = merge(snapshot.entities, test.snapshot.entities);
                    }

                    // Set unlocks
                    snapshot.unlocks = {
                        ...snapshot.unlocks,
                        ...test.snapshot.unlocks,
                    };
                }

                // Check test event
                if (settings.test && !setup.events[settings.test]) {
                    // Remove all events
                    snapshot.events = {};
                } else {
                    // Get event index
                    const eventsList = Object.values(snapshot.events);
                    const testEventIndex = eventsList.findIndex(event => event.name === settings.test);
                    const skippableEvents = eventsList.slice(0, testEventIndex).filter(event => !event.preserve);

                    // Get remove events
                    const removeEvents = skippableEvents.reduce((list, event) => [...list, ...event.removes], ['']);

                    // Check skippable events
                    skippableEvents.forEach(event => {
                        // Check removal
                        if (!event.repeat || removeEvents.includes(event.name)) {
                            const trigger = event.onTrigger.toString().replace(/[ '"]/g, '');
                            delete snapshot.events[event.name];

                            // Check for food creation
                            Array.from(trigger.matchAll(/createEntity\(food,(.+?),(.+?)\)/gm)).forEach(match => {
                                snapshot.entities.foods.active.push({
                                    type: match[1] as FoodType,
                                    props: setup.foods[match[2].split('.').pop() as string] as FoodProps,
                                } as any);
                            });

                            // Check for food removal
                            Array.from(trigger.matchAll(/removeEntity\(food,(.+?)\)/gm)).forEach(match => {
                                const foodType = match[1] as FoodType;
                                const foodIndex = snapshot.entities.foods.active.findIndex(
                                    food => food.type === foodType
                                );
                                if (foodIndex !== -1) {
                                    snapshot.entities.foods.active.splice(foodIndex, 1);
                                }
                            });

                            // Check for food calories setting
                            Array.from(trigger.matchAll(/getEntity\(food,(.+?)\).*?setCalories\((\d+)\)/gm)).forEach(
                                match => {
                                    const foodType = match[1] as FoodType;
                                    const food = snapshot.entities.foods.active.find(food => food.type === foodType) as
                                        | Food
                                        | undefined;
                                    if (food) {
                                        food.props.calories = parseInt(match[2]);
                                    }
                                }
                            );

                            // Check for unlocks
                            Array.from(trigger.matchAll(/setUnlock\((.+?),(.+?)\)/gm)).forEach(match => {
                                const unlock = match[1] as GameUnlock;
                                snapshot.unlocks[unlock] = eval(match[2]);
                            });
                        }
                    });
                }

                // Restore snapshot
                await this.restoreSnapshot(snapshot);
            }
            resolve();
        });
    };

    checkTestTurn = () => {
        const { settings } = this.props;
        const { turns } = this.state;
        const type = settings.test && setup.tests[settings.test]?.turns?.[turns];
        if (type) {
            this.createEntity('ant', type);
        }
    };

    createEntity = (category: EntityCategory, type?: EntityType | null, props?: EntityProps, state?: any) => {
        const { settings } = this.props;
        const { entities, map, unlocks, turns } = this.state;

        // Check unlocks
        if (category === 'ant' && type && type !== 'queen' && !state && !unlocks[getAntUnlock(type as AntType)]) {
            return null;
        }

        // Create entity
        let entity: Entity | undefined = undefined;
        switch (category === 'ant' || category === 'danger' ? `${category}-${type}` : category) {
            case 'ant-explorer':
                entity = new AntExplorer(this.state, props, state);
                break;
            case 'ant-queen':
                entity = new AntQueen(this.state, props, state);
                break;
            case 'ant-warrior':
                entity = new AntWarrior(this.state, props, state);
                break;
            case 'ant-worker':
                entity = new AntWorker(this.state, props, state);
                break;
            case 'danger-anteater':
                entity = new DangerAnteater(this.state, props, state);
                break;
            case 'danger-antWarrior':
                entity = new DangerAntWarrior(this.state, props, state);
                break;
            case 'danger-antWorker':
                entity = new DangerAntWorker(this.state, props, state);
                break;
            case 'danger-lawnmower':
                entity = new DangerLawnmower(this.state, props, state);
                break;
            case 'danger-magnifier':
                entity = new DangerMagnifier(this.state, props, state);
                break;
            case 'danger-spider':
                entity = new DangerSpider(this.state, props, state);
                break;
            case 'food':
                entity = new Food(this.state, type as FoodType, props as FoodProps, state);
                break;
            case 'nature':
                entity = new Nature(this.state, type as NatureType, props as NatureProps, state);
                break;
        }

        // Check existence
        if (!entity) {
            return;
        }

        // Add to Map
        map.entities.addChild(entity);

        // Check highlight
        if (
            !state &&
            entity.category === 'ant' &&
            type &&
            type !== 'queen' &&
            !entities.ants[entity.typeGroup as AntTypeGroup].created
        ) {
            void entity.highlight();
        }

        // Update entities
        this.updateEntities(entity.category, entity.type, prevState => {
            // Update active and created
            const newState: any = {
                active: [...prevState.active, entity],
                created: prevState.created + (state ? 0 : 1),
            };

            // Check mutation
            if (entity?.mutation) {
                newState.mutations = prevState.mutations + 1;
            }

            return newState;
        });

        // Update state
        if (!state && entity.category === 'ant') {
            // Update calories
            if (unlocks.colonyHealth) {
                this.setState(prevState => ({
                    calories: Math.max(
                        0,
                        prevState.calories - ((entity && setup.ants[entity.type].creationCalories) || 0)
                    ),
                }));
            }

            // Update buffs
            if (entity.type === 'explorer' && entity.setup.buff) {
                const buff: AntBuffType = entity.setup.buff;
                this.setState(prevState => ({
                    entities: {
                        ...prevState.entities,
                        ants: {
                            ...prevState.entities.ants,
                            buffs: {
                                ...prevState.entities.ants.buffs,
                                [buff]: round(prevState.entities.ants.buffs[buff] + setup.ants.buffs[buff], 2),
                            },
                        },
                    },
                }));
            }
        }

        // Debug log
        if (
            (!state && entity.category === 'ant' && settings.debugAnts) ||
            (entity.category === 'danger' && settings.debugDangers)
        ) {
            log(turns, 'Entity', `Created '${entity.name}' ${entity.index}`);
        }

        return entity;
    };

    getEntity = (category: EntityCategory, type?: EntityType | null, mutation?: EntityMutation | null) => {
        return (
            (this.state.entities[getEntityCategoryGroup(category)].active as Entity[]).find(
                entity =>
                    (entity.active || entity.type === 'cheese') &&
                    !entity.destroyed &&
                    (!mutation || entity.mutation === mutation) &&
                    (!type || entity.type === type)
            ) || null
        );
    };

    focusEntity = async (entity?: Entity, zoom?: number, follow?: boolean, highlight?: boolean) => {
        return this.state.map.focusEntity(entity, zoom, follow, highlight);
    };

    removeEntity = (category?: EntityCategory | null, type?: EntityType | null, entity?: Entity | null) => {
        const { settings } = this.props;
        const { map, turns } = this.state;

        // Check entity
        if (!entity && category) {
            entity = this.getEntity(category, type);
        }
        if (!entity) {
            return;
        }

        // Remove from map
        map.entities.removeChild(entity);
        entity.destroy();

        // Update state
        this.updateEntities(entity.category, entity.type, prevState => ({
            active: prevState.active.filter((e: any) => e !== entity),
            removed: prevState.removed + 1,
        }));

        // Debug log
        if (
            (entity.category === 'ant' && settings.debugAnts) ||
            (entity.category === 'danger' && settings.debugDangers) ||
            (entity.category === 'food' && settings.debugFoods)
        ) {
            log(turns, 'Entity', `Removed '${entity.name}' ${entity.index}`);
        }
    };

    updateEntities = (
        category: EntityCategory,
        type: EntityType | null | undefined,
        entityStateUpdate: (prevEntityState: any) => any
    ) => {
        this.setState(prevState => {
            // Get groups
            const categoryGroupName = getEntityCategoryGroup(category);
            const categoryGroup: any = prevState.entities[categoryGroupName];
            const typeGroupName = type && getEntityTypeGroup(type);
            const typeGroup = typeGroupName && categoryGroup[typeGroupName];

            // Get entities
            const entities = {
                ...prevState.entities,
                [categoryGroupName]: typeGroup
                    ? {
                          ...categoryGroup,
                          ...entityStateUpdate(categoryGroup),
                          [typeGroupName]: {
                              ...typeGroup,
                              ...entityStateUpdate(typeGroup),
                          },
                      }
                    : {
                          ...categoryGroup,
                          ...entityStateUpdate(categoryGroup),
                      },
            };

            if (category === 'ant') {
                // Get ant allocation and shares
                entities.ants.allocation = round(
                    entities.ants.explorers.allocation +
                        entities.ants.warriors.allocation +
                        entities.ants.workers.allocation,
                    1
                );
                entities.ants.explorers.share =
                    entities.ants.explorers.active.length / entities.ants.active.length || 0;
                entities.ants.warriors.share = entities.ants.warriors.active.length / entities.ants.active.length || 0;
                entities.ants.workers.share = entities.ants.workers.active.length / entities.ants.active.length || 0;
            }

            // Update state
            return {
                entities,
            };
        });
    };

    checkAntAllocation = () => {
        const {
            calories,
            caloriesCost,
            caloriesIncome,
            caloriesReserve,
            entities: { ants, natures },
            unlocks,
            turns,
        } = this.state;
        const rate = caloriesIncome - caloriesCost;

        // Check interval - update this to increase frequency of portfolio manager allocation
        const interval = clamp(Math.floor(-rate / 2) + 1, 1, 5);
        if ((!unlocks.portfolioTool && !unlocks.starvation) || turns % interval) {
            return;
        }

        // Check state to either create or kill Ants
        if (unlocks.portfolioTool && (unlocks.reserveLimit ? calories > caloriesReserve + 100 : !!calories)) {
            this.createAntByAllocation();
        } else if (ants.active.length && unlocks.starvation && rate < 0 && (!calories || calories <= caloriesReserve)) {
            // Kill faster when no Food in range
            const antColony = natures.active.find(nature => nature.type === 'antColony');
            const count = !antColony.getFoodsInRange() ? Math.ceil(ants.active.length / 10) : 1;
            for (let i = 0; i < count; i++) {
                this.killAntByAllocation();
            }
        }
    };

    createAntByAllocation = () => {
        const {
            entities: { ants },
            unlocks,
        } = this.state;
        const diffs = this.getAntAllocationDiffs();

        // Create Ant based on diffs and unlocks
        if (
            ants.explorers.allocation &&
            unlocks.antExplorer &&
            diffs.explorer > diffs.warrior &&
            diffs.explorer > diffs.worker
        ) {
            this.createEntity('ant', 'explorer');
        } else if (ants.warriors.allocation && unlocks.antWarrior && diffs.warrior > diffs.worker) {
            this.createEntity('ant', 'warrior');
        } else if (ants.workers.allocation && unlocks.antWorker) {
            this.createEntity('ant', 'worker');
        }
    };

    killAntByAllocation = () => {
        const { explorers, warriors, workers } = this.state.entities.ants;
        const diffs = this.getAntAllocationDiffs();

        // Kill Ant based on diffs
        let entity: Entity | null = null;
        if (
            explorers.active.length &&
            ((diffs.explorer < diffs.warrior && diffs.explorer < diffs.worker) ||
                (!warriors.active.length && !workers.active.length))
        ) {
            entity = explorers.active.find(explorer => !explorer.role);
        } else if (
            warriors.active.length &&
            (diffs.warrior < diffs.worker || (!explorers.active.length && !workers.active.length))
        ) {
            entity = warriors.active[0];
        } else {
            entity =
                workers.active.find(worker => !worker.target) ||
                workers.active.find(worker => !worker.carryAmount) ||
                workers.active[0];
        }

        if (entity) {
            entity.die();
        }
    };

    getAntAllocationDiffs = () => {
        const { allocation, explorers, warriors, workers } = this.state.entities.ants;

        // Get differences between Ant allocations and shares
        return {
            explorer: explorers.allocation / allocation - explorers.share,
            warrior: warriors.allocation / allocation - warriors.share,
            worker: workers.allocation / allocation - workers.share,
        };
    };

    changeAntAllocation = (antType: AntType, increase: boolean) => {
        // Update Ant allocation
        this.updateEntities('ant', antType, (prevState: GameStateAnt) => ({
            allocation: clamp(
                round(setup.antAllocationStep * (increase ? 1 : -1) + (prevState.allocation || 0), 10),
                0,
                1
            ),
        }));
    };

    addCalories = (amount: number) => {
        if (this.state.unlocks.colonyHealth) {
            // Update state
            this.setState(prevState => ({
                caloriesAdding: prevState.caloriesAdding + amount,
            }));
        }
    };

    changeCaloriesReserve = (increase: boolean) => {
        // Update state
        this.setState(prevState => ({
            caloriesReserve: setup.caloriesReserveStep * (increase ? 1 : -1) + prevState.caloriesReserve,
        }));
    };

    updateCalories = () => {
        this.setState(prevState => {
            const { explorers, warriors, workers } = prevState.entities.ants;

            // Prepare values
            let caloriesCost = 0;
            let caloriesIncome = 0;

            if (this.state.unlocks.currentRate) {
                // Get calories cost
                caloriesCost += explorers.active.length * (setup.ants.explorer.turnCalories || 0);
                caloriesCost += warriors.active.length * (setup.ants.warrior.turnCalories || 0);
                caloriesCost += workers.active.length * (setup.ants.worker.turnCalories || 0);
                caloriesCost = Math.round(caloriesCost);

                // Get calories income
                caloriesIncome = workers.active.reduce((total, worker) => total + worker.income, 0);
                caloriesIncome = Math.round(caloriesIncome);
            }

            // Update state
            return {
                calories: Math.max(0, prevState.calories + prevState.caloriesAdding - caloriesCost),
                caloriesAdding: 0,
                caloriesCost,
                caloriesIncome,
            };
        });
    };

    updateGridMatrix = () => {
        // Create matrix
        const gridMatrix = createGrid(setup.map.cells.wide, setup.map.cells.wide, 0);

        // Block foods
        this.state.entities.foods.active.forEach(food => {
            gridMatrix[food.cell.y][food.cell.x] = 1;
        });

        // Block nature
        this.state.entities.natures.active.forEach(nature => {
            if (nature.active) {
                gridMatrix[nature.cell.y][nature.cell.x] = 1;
            }
        });

        // Update state
        this.setState({
            gridMatrix,
        });
    };

    getClosestFogCell = (cell: PIXI.Point) => {
        for (let range = 1; range < setup.map.cells.wide / 2; range++) {
            for (let x = cell.x - range; x <= cell.x + range; x++) {
                for (let y = cell.y - range; y <= cell.y + range; y++) {
                    const cellName = `${x},${y}`;
                    if (this.state.fog[cellName]) {
                        return new PIXI.Point(x, y);
                    }
                }
            }
        }
        return null;
    };

    revealFog = (cell: PIXI.Point, range: number) => {
        // Get cell names
        const cellNames: string[] = [];
        range = Math.ceil(range);
        for (let x = cell.x - range; x <= cell.x + range; x++) {
            for (let y = cell.y - range; y <= cell.y + range; y++) {
                const cellName = `${x},${y}`;
                if (this.state.fog[cellName] && hypotenuse(x - cell.x, y - cell.y) < range + 0.5) {
                    cellNames.push(cellName);
                }
            }
        }

        if (cellNames.length) {
            // Update state
            this.setState(prevState => {
                // Remove cells
                const fog = {
                    ...prevState.fog,
                };
                cellNames.forEach(cellName => delete fog[cellName]);

                // Update state
                return {
                    fog,
                };
            });

            // Update map fog and foods
            this.state.map.fog.revealCells(cellNames);
            this.state.entities.foods.active.forEach(food => food.checkFound(cellNames));
        }

        return cellNames;
    };

    showPopup = (name: string) => {
        return new Promise<void>(resolve => {
            // Get popup
            const popup = this.props.popups.get(name);
            if (!popup) {
                return resolve();
            }

            // Check debug
            if (this.props.settings.debugPopups) {
                log(this.state.turns, 'Popup', `Show ${popup.type} '${name}'`);
                resolve();
            } else {
                // Check pause
                const pause = popup.type === 'dialog';
                if (pause) {
                    this.setPaused(true);
                }

                // Show popup
                this.props.popups.show(name, () => {
                    // Check pause
                    if (pause) {
                        this.setPaused(false);
                    }

                    // Resolve promise
                    resolve();
                });
            }
        });
    };

    takeSnapshot = (force?: boolean) => {
        return new Promise<GameSnapshot | null>(resolve => {
            const { calories, caloriesIncome, entities } = this.state;

            // Check conditions
            if (!force) {
                if (
                    calories < setup.snapshotMinimumCalories ||
                    caloriesIncome < setup.snapshotMinimumCaloriesIncome ||
                    entities.ants.workers.active.length < setup.snapshotMinimumAntWorkers
                ) {
                    return null;
                }

                // Check foods in range
                const foods = this.getEntity('nature', 'antColony')?.getFoodsInRange();
                const foodCalories = Array.isArray(foods) ? foods.reduce((total, food) => total + food.calories, 0) : 0;
                if (foodCalories < setup.snapshotMinimumFoodCalories) {
                    return null;
                }
            }

            // Update state
            this.setState(
                prevState => ({
                    snapshot: {
                        calories: prevState.calories,
                        caloriesReserve: prevState.caloriesReserve,
                        entities: merge(prevState.entities, {
                            ants: {
                                active: prevState.entities.ants.active
                                    .filter(ant => ant.active)
                                    .map(ant => ant.getSnapshot()),
                                explorers: {
                                    active: [],
                                },
                                queens: {
                                    active: [],
                                },
                                warriors: {
                                    active: [],
                                },
                                workers: {
                                    active: [],
                                },
                            },
                            dangers: {
                                active: prevState.entities.dangers.active
                                    .filter(danger => danger.active)
                                    .map(danger => danger.getSnapshot()),
                                anteaters: {
                                    active: [],
                                },
                                antWarriors: {
                                    active: [],
                                },
                                antWorkers: {
                                    active: [],
                                },
                                lawnmowers: {
                                    active: [],
                                },
                                magnifiers: {
                                    active: [],
                                },
                                spiders: {
                                    active: [],
                                },
                            },
                            foods: {
                                active: prevState.entities.foods.active.map(food => food.getSnapshot()),
                            },
                            natures: {
                                active: prevState.entities.natures.active.map(naature => naature.getSnapshot()),
                            },
                        }),
                        events: Object.fromEntries(
                            Object.entries(prevState.events).map(([name, event]) => [name, { ...event }])
                        ),
                        fog: prevState.fog,
                        index: prevState.snapshot.index + 1,
                        unlocks: {
                            ...prevState.unlocks,
                        },
                        turns: prevState.turns,
                    },
                }),
                () => resolve(this.state.snapshot)
            );

            // Debug log
            if (this.props.settings.debugEvents) {
                log(this.state.turns, 'Snapshot', `Taken '${this.state.snapshot.index + 1}'`);
            }
        });
    };

    restoreSnapshot = (snapshot?: GameSnapshot) => {
        return new Promise<void>(resolve => {
            // Pause
            this.setPaused(true, () => {
                // Get snapshot
                const { entities, map, snapshot: stateSnapshot } = this.state;
                const snap = snapshot || stateSnapshot;
                const naturesFilter = ['antColony', 'dangerColony', 'web'];

                // Remove active entities
                [
                    ...entities.ants.active,
                    ...entities.dangers.active,
                    ...entities.foods.active,
                    ...entities.natures.active.filter(nature => !naturesFilter.includes(nature.type)),
                ].forEach(entity => {
                    map.entities.removeChild(entity);
                    entity.destroy();
                });

                // Reset state to snapshot
                const { entities: snapshotEntities, ...snapshotState } = snap;
                this.setState(
                    prevState => ({
                        ...prevState,
                        ...snapshotState,
                        entities: merge(snapshotEntities, {
                            ants: {
                                active: [],
                                created: Math.max(
                                    snapshotEntities.ants.active.length,
                                    snapshotEntities.ants.created,
                                    snapshotEntities.ants.explorers.created +
                                        snapshotEntities.ants.queens.created +
                                        snapshotEntities.ants.warriors.created +
                                        snapshotEntities.ants.workers.created
                                ),
                                removed:
                                    snapshotEntities.ants.explorers.removed +
                                    snapshotEntities.ants.queens.removed +
                                    snapshotEntities.ants.warriors.removed +
                                    snapshotEntities.ants.workers.removed,
                                explorers: {
                                    created: Math.max(
                                        snapshotEntities.ants.active.filter(ant => ant.type === 'explorer').length,
                                        snapshotEntities.ants.explorers.created
                                    ),
                                },
                                queens: {
                                    created: Math.max(
                                        snapshotEntities.ants.active.filter(ant => ant.type === 'queen').length,
                                        snapshotEntities.ants.queens.created
                                    ),
                                },
                                warriors: {
                                    created: Math.max(
                                        snapshotEntities.ants.active.filter(ant => ant.type === 'warrior').length,
                                        snapshotEntities.ants.warriors.created
                                    ),
                                },
                                workers: {
                                    created: Math.max(
                                        snapshotEntities.ants.active.filter(ant => ant.type === 'worker').length,
                                        snapshotEntities.ants.workers.created
                                    ),
                                },
                            },
                            dangers: {
                                active: [],
                                created: Math.max(
                                    snapshotEntities.dangers.active.length,
                                    snapshotEntities.dangers.created,
                                    snapshotEntities.dangers.anteaters.created +
                                        snapshotEntities.dangers.antWarriors.created +
                                        snapshotEntities.dangers.antWorkers.created +
                                        snapshotEntities.dangers.lawnmowers.created +
                                        snapshotEntities.dangers.magnifiers.created +
                                        snapshotEntities.dangers.spiders.created
                                ),
                                removed:
                                    snapshotEntities.dangers.anteaters.removed +
                                    snapshotEntities.dangers.antWarriors.removed +
                                    snapshotEntities.dangers.antWorkers.removed +
                                    snapshotEntities.dangers.lawnmowers.removed +
                                    snapshotEntities.dangers.magnifiers.removed +
                                    snapshotEntities.dangers.spiders.removed,
                                anteaters: {
                                    created: Math.max(
                                        snapshotEntities.dangers.active.filter(danger => danger.type === 'anteater')
                                            .length,
                                        snapshotEntities.dangers.anteaters.created
                                    ),
                                },
                                antWarriors: {
                                    created: Math.max(
                                        snapshotEntities.dangers.active.filter(danger => danger.type === 'antWarrior')
                                            .length,
                                        snapshotEntities.dangers.antWarriors.created
                                    ),
                                },
                                antWorkers: {
                                    created: Math.max(
                                        snapshotEntities.dangers.active.filter(danger => danger.type === 'antWorker')
                                            .length,
                                        snapshotEntities.dangers.antWorkers.created
                                    ),
                                },
                                lawnmowers: {
                                    created: Math.max(
                                        snapshotEntities.dangers.active.filter(danger => danger.type === 'lawnmower')
                                            .length,
                                        snapshotEntities.dangers.lawnmowers.created
                                    ),
                                },
                                magnifiers: {
                                    created: Math.max(
                                        snapshotEntities.dangers.active.filter(danger => danger.type === 'magnifier')
                                            .length,
                                        snapshotEntities.dangers.magnifiers.created
                                    ),
                                },
                                spiders: {
                                    created: Math.max(
                                        snapshotEntities.dangers.active.filter(danger => danger.type === 'spider')
                                            .length,
                                        snapshotEntities.dangers.spiders.created
                                    ),
                                },
                            },
                            foods: {
                                active: [],
                            },
                            natures: {
                                active: entities.natures.active.filter(nature => naturesFilter.includes(nature.type)),
                            },
                        }),
                    }),
                    () => {
                        // Add snapshot entities
                        (['explorer', 'queen', 'warrior', 'worker'] as AntType[]).forEach(antType => {
                            const antTypeGroup = getEntityTypeGroup<AntTypeGroup>(antType);
                            snapshotEntities.ants.active
                                .filter(antSnapshot => antSnapshot.type === antType)
                                .forEach((antSnapshot, index, antSnapshots) => {
                                    this.createEntity('ant', antSnapshot.type, antSnapshot.props, {
                                        ...antSnapshot.state,
                                        index:
                                            this.state.entities.ants[antTypeGroup].created -
                                            antSnapshots.length +
                                            index +
                                            1,
                                    });
                                });
                        });
                        (
                            ['anteater', 'antWarrior', 'antWorker', 'lawnmower', 'magnifier', 'spider'] as DangerType[]
                        ).forEach(dangerType => {
                            const dangerTypeGroup = getEntityTypeGroup<DangerTypeGroup>(dangerType);
                            snapshotEntities.dangers.active
                                .filter(dangerSnapshot => dangerSnapshot.type === dangerType)
                                .forEach((dangerSnapshot, index, dangerSnapshots) => {
                                    this.createEntity('danger', dangerSnapshot.type, dangerSnapshot.props, {
                                        ...dangerSnapshot.state,
                                        index:
                                            this.state.entities.dangers[dangerTypeGroup].created -
                                            dangerSnapshots.length +
                                            index +
                                            1,
                                    });
                                });
                        });
                        snapshotEntities.foods.active.forEach((foodSnapshot, index) => {
                            this.createEntity('food', foodSnapshot.type, foodSnapshot.props, {
                                ...foodSnapshot.state,
                                index:
                                    this.state.entities.foods.created -
                                    snapshotEntities.foods.active.length +
                                    index +
                                    1,
                            });
                        });
                        snapshotEntities.natures.active
                            .filter(nature => !nature.type.endsWith('Colony'))
                            .forEach((natureSnapshot, index) => {
                                this.createEntity('nature', natureSnapshot.type, natureSnapshot.props, {
                                    ...natureSnapshot.state,
                                    index:
                                        this.state.entities.natures.created -
                                        snapshotEntities.natures.active.length +
                                        index +
                                        1,
                                });
                            });

                        // Update map
                        map.updateGameState(this.state);
                        map.fog.syncState();

                        // Debug log
                        if (this.props.settings.debugEvents) {
                            log(this.state.turns, 'Snapshot', `Restored '${snap.index}'`);
                        }

                        // Continue
                        this.setPaused(false, resolve);
                    }
                );
            });
        });
    };

    retry = () => {
        // Update state
        this.setState(prevState => ({
            retries: prevState.retries + 1,
        }));

        // Restore previous snapshot
        return this.restoreSnapshot();
    };

    setPaused = (paused: boolean, callback?: any) => {
        if (!this.state.finished) {
            // Check gsap and frame requests
            if (paused) {
                gsap.globalTimeline.pause();
                PIXI.Ticker.shared.stop();

                // Clear request
                if (this.frameRequest) {
                    window.cancelAnimationFrame(this.frameRequest);
                    this.frameRequest = null;
                }
            } else if (!this.frameRequest) {
                gsap.globalTimeline.resume();
                PIXI.Ticker.shared.start();
                this.previousTime = 0;
                this.tick();
            }

            // Update state
            this.setState(
                {
                    paused,
                },
                callback
            );
        }
    };

    setSpeed = (speed: number) => {
        const { min, max } = setup.map.speed;
        const value = clamp(speed, min, max);

        gsap.globalTimeline.timeScale(value);
        this.setState({
            speed: value,
        });
    };

    setUnlock = (name: GameUnlock, unlocked: boolean) => {
        // Update state
        this.setState(
            prevState => ({
                unlocks: {
                    ...prevState.unlocks,
                    [name]: unlocked,
                },
            }),
            () => {
                void this.takeSnapshot();
            }
        );

        // Debug log
        if (this.props.settings.debugEvents) {
            log(this.state.turns, 'Unlock', `${unlocked ? 'Unlocked' : 'Locked'} '${name}'`);
        }
    };

    tick = (time?: number) => {
        // Request next frame
        this.frameRequest = window.requestAnimationFrame(this.tick);
        if (!time) {
            return;
        }

        // Update time
        if (this.previousTime) {
            this.tickTime += ((time - this.previousTime) / 1000) * this.state.speed;
        }
        this.previousTime = time;

        // Check turn
        if (this.tickTime >= 1) {
            this.tickTime--;

            // Update state
            this.setState(prevState => ({
                turns: prevState.turns + 1,
            }));
        }
    };

    render() {
        return (
            <GameContext.Provider value={this.state}>
                {this.state.finished && <Confetti style={{ left: 0, position: 'fixed', top: 0, zIndex: 1000 }} />}
                {this.props.children}
            </GameContext.Provider>
        );
    }
}

export const GameProvider = compose(withApi, withAnalytics, withPopups, withSettings, withSounds)(Game);
